/**
 * Accessibility focus utilities for overlays/modals.
 * Exposed on window for use by theme.js, global.js, pickup-availability.js, etc.
 */
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** 元素是否被隐藏（自身或任意祖先 aria-hidden="true"，或 CSS 隐藏） */
function isFocusableVisible(el) {
  if (el.disabled || el.getAttribute('aria-hidden') === 'true') return false;
  let node = el;
  while (node && node !== document.body) {
    if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') return false;
    node = node.parentElement;
  }
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function getFocusableElements(container) {
  if (!container || !container.querySelectorAll) return [];
  const nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
  return Array.prototype.filter.call(nodes, isFocusableVisible);
}

function trapFocus(container) {
  return function (e) {
    if (e.key !== 'Tab') return;
    if (!container || !container.contains(document.activeElement)) {
      e.preventDefault();
      moveFocusToFirst(container);
      return;
    }
    const focusable = getFocusableElements(container);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
}

function moveFocusToFirst(container) {
  if (!container) return;
  const focusable = getFocusableElements(container);
  if (focusable.length > 0) {
    focusable[0].focus();
  } else {
    if (typeof container.focus === 'function') {
      container.focus();
    } else {
      const wrapper = container.querySelector('[tabindex="-1"]') || container;
      if (wrapper && typeof wrapper.focus === 'function') wrapper.focus();
    }
  }
}

function returnFocus(opener) {
  if (opener && typeof opener.focus === 'function') {
    try {
      opener.focus();
    } catch (err) {
      // ignore
    }
  }
}

window.A11yFocusUtils = {
  getFocusableElements,
  trapFocus,
  moveFocusToFirst,
  returnFocus
};

/** Popup-addons: 当弹框仅通过 focus/hover 显示（未走 tingle 模态）时，把焦点限制在弹框内并关闭时恢复焦点。必须绑在 document 上才能拦截到「焦点在弹框外」时的 Tab。 */
function setupPopupAddonsFocusTrap(addon, content, moveFocusInto) {
  if (!content || !window.A11yFocusUtils) return;
  if (content.closest('.tingle-modal')) return; // 已由 global.js 的模态做 focus trap
  if (addon._a11yPopupAddonsTrapHandler) return; // 已设置过
  const opener = document.activeElement;
  addon._a11yPopupAddonsOpener = opener;
  addon._a11yPopupAddonsContent = content;
  if (!content.hasAttribute('tabindex')) content.setAttribute('tabindex', '-1');
  const handler = window.A11yFocusUtils.trapFocus(content);
  addon._a11yPopupAddonsTrapHandler = handler;
  document.addEventListener('keydown', handler, true);
  if (moveFocusInto) window.A11yFocusUtils.moveFocusToFirst(content);
}

function teardownPopupAddonsFocusTrap(addon) {
  if (!addon || !addon._a11yPopupAddonsTrapHandler) return;
  document.removeEventListener('keydown', addon._a11yPopupAddonsTrapHandler, true);
  if (window.A11yFocusUtils && addon._a11yPopupAddonsOpener) {
    window.A11yFocusUtils.returnFocus(addon._a11yPopupAddonsOpener);
  }
  addon._a11yPopupAddonsTrapHandler = null;
  addon._a11yPopupAddonsContent = null;
  addon._a11yPopupAddonsOpener = null;
}

document.addEventListener('DOMContentLoaded', () => {
  // WCAG 1.4.13: Content on Hover or Focus
  // Requirement: Dismissible, Hoverable, Persistent

  /**
   * Helper to handle safe dismissal (ESC key)
   */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      let dismissed = false;

      // 1. Tooltips: dismiss the tooltip that currently has focus (reliable without :hover/:focus-within)
      const activeTooltip = document.activeElement && document.activeElement.closest('.tooltip');
      if (activeTooltip) {
        const c = activeTooltip.querySelector('.tooltip-content');
        if (c) c.setAttribute('aria-hidden', 'true');
        activeTooltip.classList.add('tooltip-dismissed');
        activeTooltip.classList.remove('open');
        dismissed = true;
      }
      if (!dismissed) {
        const tooltips = document.querySelectorAll('.tooltip');
        tooltips.forEach(tooltip => {
          if (tooltip.matches(':hover') || tooltip.classList.contains('open')) {
            const c = tooltip.querySelector('.tooltip-content');
            if (c) c.setAttribute('aria-hidden', 'true');
            tooltip.classList.add('tooltip-dismissed');
            tooltip.classList.remove('open');
            dismissed = true;
          }
        });
      }

      // 2. Menus / Dropdowns
      const visibleMenus = document.querySelectorAll('.dropdown-menu.visible, .mega-menu.visible, .menu-parent.visible, .submenu.visible');
      visibleMenus.forEach(menu => {
         menu.classList.remove('visible', 'open');
         if (menu.parentElement) {
             menu.parentElement.classList.remove('visible', 'open');
             // Update aria-expanded if exists
             const trigger = menu.parentElement.querySelector('[aria-expanded]');
             if (trigger) trigger.setAttribute('aria-expanded', 'false');
         }
         dismissed = true;
      });

      // 3. Search Popup
      const searchPopups = document.querySelectorAll('.header_search.search_type_popup, .search-popup, #search-drawer');
      searchPopups.forEach(popup => {
          if (popup.classList.contains('open') || document.documentElement.classList.contains('open-search')) {
              if (window.__searchTrap) {
                  window.__searchTrap.container.removeEventListener('keydown', window.__searchTrap.handler);
                  window.__searchTrap = null;
              }
              if (window.A11yFocusUtils && window.__searchFocusReturn) {
                  window.A11yFocusUtils.returnFocus(window.__searchFocusReturn);
              }
              window.__searchFocusReturn = null;
              const closeBtn = popup.querySelector('.btn-search-close, .close-search');
              if (closeBtn) {
                  closeBtn.click();
              } else {
                  document.documentElement.classList.remove('open-search');
                  popup.classList.remove('open');
              }
              dismissed = true;
          }
      });
      
      // 4. Recommendation Popup / ModalLightbox / FilterSidebar
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      dialogs.forEach(dialog => {
          if (dialog.style.display !== 'none' && !dialog.classList.contains('hidden') && dialog.offsetParent !== null) {
             const closeBtn = dialog.querySelector('.close, .popup-close-btn, .tingle-modal__close');
             if (closeBtn) closeBtn.click();
             dismissed = true;
          }
      });
      
       // 5. Sticky Bar
       const stickyBar = document.querySelector('#sticky-bar');
       if(stickyBar && stickyBar.classList.contains('open')) {
           stickyBar.classList.remove('open');
           dismissed = true;
       }

       // 6. Sort Dropdown
       const sortDropdowns = document.querySelectorAll('select-sorter');
       sortDropdowns.forEach(dropdown => {
           if (dropdown.getAttribute('aria-expanded') === 'true') {
               dropdown.setAttribute('aria-expanded', 'false');
               dismissed = true;
           }
       });

       // 6b. Account dropdown (theme uses is-open)
       const accountWrapper = document.querySelector('[data-account-dropdown-wrapper]');
       if (accountWrapper) {
           const trigger = accountWrapper.querySelector('[data-account-dropdown-trigger]');
           const menu = accountWrapper.querySelector('[data-account-dropdown-menu]');
           if (menu && (menu.classList.contains('is-open') || trigger?.getAttribute('aria-expanded') === 'true')) {
               menu.hidden = true;
               menu.classList.remove('is-open');
               if (trigger) trigger.setAttribute('aria-expanded', 'false');
               if (window.A11yFocusUtils && accountWrapper._a11yOpener) {
                   window.A11yFocusUtils.returnFocus(accountWrapper._a11yOpener);
               }
               dismissed = true;
           }
       }

       // 7. Popup Addons
       const popupAddons = document.querySelectorAll('popup-addons');
       popupAddons.forEach(addon => {
           const content = addon.querySelector('.popup-addons__content');
           if (content && !content.classList.contains('hidden')) {
               teardownPopupAddonsFocusTrap(addon);
               content.classList.add('hidden');
               dismissed = true;
           }
       });
    }
  });

  /**
   * Event Delegation for Tooltips, Popups, etc.
   * Handles dynamic content better.
   */
  
  // Focus In
  document.addEventListener('focusin', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip) {
          const content = tooltip.querySelector('.tooltip-content');
          if (content) {
              if (!content.id) {
                  content.id = `tooltip-content-${Math.random().toString(36).substr(2, 9)}`;
                  tooltip.setAttribute('aria-describedby', content.id);
              }
              content.setAttribute('role', 'tooltip');
              content.removeAttribute('aria-hidden');
          }
          tooltip.classList.remove('tooltip-dismissed');
          tooltip.classList.add('open');
      }
      

      // Sort Dropdown
      const sorter = e.target.closest('select-sorter');
      if (sorter) {
          sorter.setAttribute('aria-expanded', 'true');
      }
  });

  // Focus Out
  document.addEventListener('focusout', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip) {
          if (!tooltip.contains(e.relatedTarget)) {
              const content = tooltip.querySelector('.tooltip-content');
              if (content) content.setAttribute('aria-hidden', 'true');
              tooltip.classList.remove('open');
          }
      }
      
  });

  // Mouse Over
  document.addEventListener('mouseover', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip) {
          tooltip.classList.remove('tooltip-dismissed');
      }
      
      // Popup Addons: 不在 hover 时内联显示，避免黑一块；仅点击打开 tingle 弹窗
  });

  // Click Outside (for Sort Dropdown)
  document.addEventListener('click', (e) => {
       const sorter = e.target.closest('select-sorter');
       document.querySelectorAll('select-sorter[aria-expanded="true"]').forEach(s => {
           if (s !== sorter) {
               s.setAttribute('aria-expanded', 'false');
           }
       });
  });

  /**
   * Search Popup Initialization
   */
   const searchTriggers = document.querySelectorAll('.header-search-popup, .header_search, .btn-search');
   searchTriggers.forEach(trigger => {
       if (!trigger.hasAttribute('aria-expanded')) {
           trigger.setAttribute('aria-expanded', 'false');
       }
       trigger.addEventListener('click', () => {
           setTimeout(() => {
               const isOpen = document.documentElement.classList.contains('open-search') || trigger.classList.contains('open');
               trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
           }, 50);
       });
   });

   /**
    * Recommendation Popup Observer: trap focus when shown, return focus and remove trap when hidden.
    */
    const recommendationPopups = document.querySelectorAll('.recommendation-popup');
    recommendationPopups.forEach(popup => {
        let wasVisible = popup.style.display !== 'none' && !popup.classList.contains('hidden');
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
                    const isVisible = popup.style.display !== 'none' && !popup.classList.contains('hidden');
                    if (isVisible && !wasVisible) {
                        popup._a11yOpener = document.activeElement;
                        moveFocusToFirst(popup);
                        if (!popup.hasAttribute('data-focus-trap')) {
                            popup.setAttribute('data-focus-trap', 'true');
                            const handler = trapFocus(popup);
                            popup._a11yTrapHandler = handler;
                            document.addEventListener('keydown', handler, true);
                        }
                    } else if (!isVisible && wasVisible) {
                        if (popup._a11yTrapHandler) {
                            document.removeEventListener('keydown', popup._a11yTrapHandler, true);
                            popup._a11yTrapHandler = null;
                        }
                        popup.removeAttribute('data-focus-trap');
                        if (window.A11yFocusUtils && popup._a11yOpener) {
                            window.A11yFocusUtils.returnFocus(popup._a11yOpener);
                        }
                        popup._a11yOpener = null;
                    }
                    wasVisible = isVisible;
                }
            });
        });
        observer.observe(popup, { attributes: true });
    });
});
